/**
 * Validation utilities for form fields
 * Provides common validation functions for the Kiwi Voyages website
 */

import { ValidationResult } from '../types/validation';

/**
 * Validates that a field is not empty
 */
export function validateRequired(value: string, message = 'Ce champ est requis'): ValidationResult {
  const isValid = value.trim().length > 0;
  const result: ValidationResult = {
    field: 'generic',
    isValid
  };
  if (!isValid) {
    result.message = message;
  }
  return result;
}

/**
 * Validates email format
 */
export function validateEmail(email: string, message = 'Adresse email invalide'): ValidationResult {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isValid = emailRegex.test(email.trim());
  const result: ValidationResult = {
    field: 'email',
    isValid
  };
  if (!isValid) {
    result.message = message;
  }
  return result;
}

/**
 * Validates phone number format (French format)
 */
export function validatePhone(phone: string, message = 'Numéro de téléphone invalide'): ValidationResult {
  const phoneRegex = /^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/;
  const isValid = phoneRegex.test(phone.trim());
  const result: ValidationResult = {
    field: 'phone',
    isValid
  };
  if (!isValid) {
    result.message = message;
  }
  return result;
}

/**
 * Validates that a date is valid and within acceptable range
 */
export function validateDate(
  date: Date | string, 
  minDate?: Date, 
  maxDate?: Date, 
  _message = 'Date invalide'
): ValidationResult {
  let dateObj: Date;
  
  if (typeof date === 'string') {
    dateObj = new Date(date);
  } else {
    dateObj = date;
  }
  
  // Check if date is valid
  if (isNaN(dateObj.getTime())) {
    return {
      field: 'date',
      isValid: false,
      message: 'Date invalide'
    };
  }
  
  // Check minimum date
  if (minDate && dateObj < minDate) {
    return {
      field: 'date',
      isValid: false,
      message: `La date doit être après le ${minDate.toLocaleDateString('fr-FR')}`
    };
  }
  
  // Check maximum date
  if (maxDate && dateObj > maxDate) {
    return {
      field: 'date',
      isValid: false,
      message: `La date doit être avant le ${maxDate.toLocaleDateString('fr-FR')}`
    };
  }
  
  return {
    field: 'date',
    isValid: true
  };
}

/**
 * Validates that a number is within specified range
 */
export function validateNumber(
  value: number, 
  min?: number, 
  max?: number, 
  _message = 'Nombre invalide'
): ValidationResult {
  // Check if it's a valid number
  if (isNaN(value) || !isFinite(value)) {
    return {
      field: 'number',
      isValid: false,
      message: 'Valeur numérique invalide'
    };
  }
  
  // Check minimum value
  if (min !== undefined && value < min) {
    return {
      field: 'number',
      isValid: false,
      message: `La valeur doit être supérieure ou égale à ${min}`
    };
  }
  
  // Check maximum value
  if (max !== undefined && value > max) {
    return {
      field: 'number',
      isValid: false,
      message: `La valeur doit être inférieure ou égale à ${max}`
    };
  }
  
  return {
    field: 'number',
    isValid: true
  };
}

/**
 * Validates string length
 */
export function validateLength(
  value: string, 
  minLength?: number, 
  maxLength?: number, 
  _message = 'Longueur invalide'
): ValidationResult {
  const length = value.trim().length;
  
  // Check minimum length
  if (minLength !== undefined && length < minLength) {
    return {
      field: 'length',
      isValid: false,
      message: `Minimum ${minLength} caractères requis`
    };
  }
  
  // Check maximum length
  if (maxLength !== undefined && length > maxLength) {
    return {
      field: 'length',
      isValid: false,
      message: `Maximum ${maxLength} caractères autorisés`
    };
  }
  
  return {
    field: 'length',
    isValid: true
  };
}

/**
 * Validates that a value matches a specific pattern
 */
export function validatePattern(
  value: string, 
  pattern: RegExp, 
  message = 'Format invalide'
): ValidationResult {
  const isValid = pattern.test(value);
  const result: ValidationResult = {
    field: 'pattern',
    isValid
  };
  if (!isValid) {
    result.message = message;
  }
  return result;
}

/**
 * Validates that two values match (useful for password confirmation)
 */
export function validateMatch(
  value1: string, 
  value2: string, 
  message = 'Les valeurs ne correspondent pas'
): ValidationResult {
  const isValid = value1 === value2;
  const result: ValidationResult = {
    field: 'match',
    isValid
  };
  if (!isValid) {
    result.message = message;
  }
  return result;
}

/**
 * Validates a destination string (basic validation for travel destinations)
 */
export function validateDestination(
  destination: string, 
  _message = 'Destination invalide'
): ValidationResult {
  const trimmed = destination.trim();
  
  // Must not be empty
  if (trimmed.length === 0) {
    return {
      field: 'destination',
      isValid: false,
      message: 'La destination est requise'
    };
  }
  
  // Must be at least 2 characters
  if (trimmed.length < 2) {
    return {
      field: 'destination',
      isValid: false,
      message: 'La destination doit contenir au moins 2 caractères'
    };
  }
  
  // Must not be too long
  if (trimmed.length > 100) {
    return {
      field: 'destination',
      isValid: false,
      message: 'La destination ne peut pas dépasser 100 caractères'
    };
  }
  
  // Basic pattern check (letters, spaces, hyphens, apostrophes)
  const destinationPattern = /^[a-zA-ZÀ-ÿ\s\-'.,()]+$/;
  if (!destinationPattern.test(trimmed)) {
    return {
      field: 'destination',
      isValid: false,
      message: 'La destination contient des caractères non autorisés'
    };
  }
  
  return {
    field: 'destination',
    isValid: true
  };
}

/**
 * Combines multiple validation results
 */
export function combineValidationResults(results: ValidationResult[]): ValidationResult {
  const hasErrors = results.some(result => !result.isValid);
  const errorMessages = results
    .filter(result => !result.isValid && result.message)
    .map(result => result.message!)
    .join(', ');
  
  const result: ValidationResult = {
    field: 'combined',
    isValid: !hasErrors
  };
  
  if (hasErrors && errorMessages) {
    result.message = errorMessages;
  }
  
  return result;
}

/**
 * Debounced validation function for real-time validation
 */
export function createDebouncedValidator(
  validationFn: () => ValidationResult,
  delay = 300
): () => void {
  let timeoutId: number;
  
  return () => {
    clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => {
      validationFn();
    }, delay);
  };
}